import { useCallback, useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { Loader2, Users, Eye, MousePointerClick, UserPlus, Activity, Monitor, Smartphone, Tablet, Globe, Award, Flame, Infinity as InfinityIcon } from "lucide-react";
import { api } from "@/lib/api";

const RANGES = [
  { key: "today", label: "Aujourd'hui" },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
];

const PIE_COLORS = ["#fbbf24", "#38bdf8", "#a78bfa", "#34d399", "#f472b6", "#fb923c", "#60a5fa", "#f87171"];

const deviceIcon = (name) => {
  if (name === "Mobile") return <Smartphone size={14} />;
  if (name === "Tablette") return <Tablet size={14} />;
  if (name === "Ordinateur") return <Monitor size={14} />;
  return <Globe size={14} />;
};

const Kpi = ({ icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {icon} {label}
    </div>
    <div className="mt-2 text-3xl font-semibold text-foreground">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const Panel = ({ title, right, children, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const BreakdownList = ({ items, total }) => (
  <div className="space-y-2">
    {(items || []).map((it, i) => {
      const pct = total ? Math.round((it.count / total) * 100) : 0;
      return (
        <div key={it.name || i} className="text-sm">
          <div className="flex items-center justify-between text-foreground">
            <span className="flex items-center gap-1.5 truncate">{it.name}</span>
            <span className="text-muted-foreground">{it.count} · {pct}%</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
          </div>
        </div>
      );
    })}
    {(!items || !items.length) && <p className="text-sm text-muted-foreground">Aucune donnée.</p>}
  </div>
);

const Donut = ({ data }) => {
  const chart = (data || []).map((d) => ({ name: d.name, value: d.count }));
  if (!chart.length) return <p className="text-sm text-muted-foreground">Aucune donnée.</p>;
  return (
    <div className="flex items-center gap-4">
      <div className="h-[140px] w-[140px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chart} dataKey="value" nameKey="name" innerRadius={40} outerRadius={64} paddingAngle={2} stroke="none">
              {chart.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 space-y-1.5">
        {chart.map((e, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-foreground">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
              {e.name}
            </span>
            <span className="text-muted-foreground">{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [amb, setAmb] = useState(null);

  const load = useCallback(async (r) => {
    try {
      const res = await api.get(`/admin/analytics?range=${r}`);
      setData(res.data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
    try { const a = await api.get("/admin/ambassadors/stats"); setAmb(a.data); } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    load(range);
    const t = setInterval(() => load(range), 15000); // temps réel : rafraîchit toutes les 15 s
    return () => clearInterval(t);
  }, [range, load]);

  if (loading && !data) {
    return <div className="flex justify-center py-16" data-testid="analytics-loading"><Loader2 className="animate-spin text-amber-300" /></div>;
  }
  if (!data) return <p className="py-12 text-center text-sm text-muted-foreground">Aucune donnée d'analyse pour le moment.</p>;

  const t = data.totals || {};
  const chanTotal = (data.channels || []).reduce((a, c) => a + c.count, 0);

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      {/* En-tête : sélecteur de période + temps réel */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5" data-testid="analytics-realtime">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-sm font-medium text-emerald-200">{data.realtime_active} visiteur(s) en ce moment</span>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${range === r.key ? "border-amber-300/60 bg-amber-300/15 text-amber-100" : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20"}`}
              data-testid={`analytics-range-${r.key}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testid="kpi-visitors" icon={<Users size={14} />} label="Visiteurs uniques" value={t.visitors ?? 0} sub={`${t.new_visitors ?? 0} nouveaux`} />
        <Kpi testid="kpi-visits" icon={<Activity size={14} />} label="Visites" value={t.visits ?? 0} sub={`${t.pages_per_visit ?? 0} pages / visite`} />
        <Kpi testid="kpi-pageviews" icon={<Eye size={14} />} label="Pages vues" value={t.pageviews ?? 0} />
        <Kpi testid="kpi-today" icon={<UserPlus size={14} />} label="Aujourd'hui" value={t.visitors_today ?? 0} sub={`${t.pv_today ?? 0} pages vues`} />
      </div>

      {/* Courbe visites / pages vues */}
      <Panel title="Évolution des visites" testid="analytics-series">
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series || []} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="gpv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gvi" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
              <Tooltip contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="pageviews" name="Pages vues" stroke="#fbbf24" strokeWidth={2} fill="url(#gpv)" />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#38bdf8" strokeWidth={2} fill="url(#gvi)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-full bg-amber-400" /> Pages vues</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-full bg-sky-400" /> Visites</span>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Appareils */}
        <Panel title="Type d'appareil" testid="analytics-devices"><Donut data={data.devices} /></Panel>
        {/* Canaux */}
        <Panel title="Canal d'acquisition" testid="analytics-channels">
          <BreakdownList items={data.channels} total={chanTotal} />
        </Panel>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Pages consultées */}
        <Panel title="Pages les plus consultées" testid="analytics-top-pages">
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data.top_pages || []).map((p) => ({ name: p.label, count: p.count }))} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#d1d5db", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count" name="Vues" fill="#fbbf24" radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        {/* Navigateurs + OS + En ce moment */}
        <div className="space-y-4">
          <Panel title="Navigateurs" testid="analytics-browsers">
            <BreakdownList items={data.browsers} total={(data.browsers || []).reduce((a, c) => a + c.count, 0)} />
          </Panel>
          <Panel title="Système / appareil" testid="analytics-os">
            <BreakdownList items={data.os} total={(data.os || []).reduce((a, c) => a + c.count, 0)} />
          </Panel>
          <Panel title="En ce moment (5 min)" testid="analytics-active-pages">
            {(data.active_pages || []).length ? (
              <div className="space-y-1.5">
                {data.active_pages.map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-sm text-foreground">
                    <span className="truncate">{p.label}</span>
                    <span className="text-emerald-300">{p.count}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">Personne pour l'instant.</p>}
          </Panel>
        </div>
      </div>

      {/* Sources référentes */}
      <Panel title="Sites référents" testid="analytics-referrers">
        {(data.referrers || []).length ? (
          <div className="space-y-2">
            {data.referrers.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate text-sky-300">{r.referrer}</span>
                <span className="text-muted-foreground">{r.count}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground">Aucun site référent (trafic direct / recherche).</p>}
      </Panel>

      {/* Ambassadeurs : partage & utilisation */}
      {amb && amb.total_links > 0 && (
        <Panel title="Ambassadeurs — partage & utilisation" testid="analytics-ambassadors">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi testid="amb-kpi-links" icon={<Award size={14} />} label="Liens" value={amb.total_links} sub={`${amb.active_links} actif(s)`} />
            <Kpi testid="amb-kpi-clicks" icon={<MousePointerClick size={14} />} label="Clics" value={amb.total_clicks} />
            <Kpi testid="amb-kpi-signups" icon={<UserPlus size={14} />} label="Comptes ouverts" value={amb.total_signups} />
            <Kpi testid="amb-kpi-used" icon={<Flame size={14} />} label="Bougies allumées" value={amb.candles_used} />
            <Kpi testid="amb-kpi-remaining" icon={<InfinityIcon size={14} />} label="Bougies restantes" value={amb.candles_remaining} />
            <Kpi testid="amb-kpi-photos" icon={<Eye size={14} />} label="Avec photo" value={amb.total_photos ?? 0} />
            <Kpi testid="amb-kpi-shares" icon={<MousePointerClick size={14} />} label="Partages" value={amb.total_shares ?? 0} />
          </div>
          {(amb.per_link || []).length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="amb-per-link-table">
                <thead>
                  <tr className="border-b border-white/10 text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Lien</th>
                    <th className="py-2 pr-3 text-right">Clics</th>
                    <th className="py-2 pr-3 text-right">Comptes</th>
                    <th className="py-2 pr-3 text-right">Allumées</th>
                    <th className="py-2 pr-3 text-right">Photos</th>
                    <th className="py-2 pr-3 text-right">Partages</th>
                    <th className="py-2 text-right">Restantes</th>
                  </tr>
                </thead>
                <tbody>
                  {amb.per_link.map((l) => (
                    <tr key={l.code} className="border-b border-white/5" data-testid="amb-per-link-row">
                      <td className="py-2 pr-3">
                        <span className="font-mono text-xs text-sky-200">{l.code}</span>
                        {l.label && <span className="ml-2 text-muted-foreground">{l.label}</span>}
                        <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] ${l.expired || !l.active ? "bg-amber-400/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{!l.active ? "off" : l.expired ? "expiré" : "actif"}</span>
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{l.clicks}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{l.signups}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{l.used}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{l.photos ?? 0}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{l.shares ?? 0}</td>
                      <td className="py-2 text-right tabular-nums">{l.remaining}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      <p className="text-center text-xs text-muted-foreground">Mise à jour automatique toutes les 15 s · dernière : {new Date(data.generated_at).toLocaleTimeString("fr-FR")}</p>
    </div>
  );
};
